import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';

import { formatEvvCaptured, formatEvvDate } from '../utils';

import type { EvvSessionTableProps } from '../types';
import type { ChangeEvent } from 'react';

export function EvvSessionTable({ sessions, selectedSessionId, onSelect }: EvvSessionTableProps) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  useEffect(() => setPage(0), [sessions]);

  function changeRowsPerPage(event: ChangeEvent<HTMLInputElement>) {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  }

  return (
    <Card sx={{ flexShrink: 0 }}>
      <TableContainer>
        <Table size="small" aria-label="EVV sessions">
          <TableHead>
            <TableRow>
              <TableCell>Session</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Provider</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>EVV required</TableCell>
              <TableCell>Start captured</TableCell>
              <TableCell>End captured</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((session) => (
              <TableRow hover selected={session.id === selectedSessionId} key={session.id}>
                <TableCell>{session.id}</TableCell>
                <TableCell>
                  {`${session.client?.first_name ?? ''} ${session.client?.last_name ?? ''}`.trim() ||
                    session.client_id}
                </TableCell>
                <TableCell>
                  {`${session.session_provider?.first_name ?? ''} ${session.session_provider?.last_name ?? ''}`.trim() ||
                    '—'}
                </TableCell>
                <TableCell>{formatEvvDate(String(session.date_start))}</TableCell>
                <TableCell>{session.status}</TableCell>
                <TableCell>{session.evv_required ? 'Yes' : 'No'}</TableCell>
                <TableCell>
                  {formatEvvCaptured(session.evv_start_captured, session.evv_start_fallback_reason)}
                </TableCell>
                <TableCell>
                  {formatEvvCaptured(session.evv_end_captured, session.evv_end_fallback_reason)}
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    aria-label={`Inspect session ${session.id}`}
                    onClick={() => onSelect(session.id)}
                  >
                    Inspect
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={sessions.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        onRowsPerPageChange={changeRowsPerPage}
        rowsPerPageOptions={[25, 50, 100]}
      />
    </Card>
  );
}
